import React, { useState } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  CircularProgress,
  Grid,
  Alert,
  IconButton,
  InputAdornment
} from "@mui/material";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { readFileAsText } from "../../utils/fileHandling";

const DecryptionForm = ({
  encryptKey,
  setEncryptKey,
  encryptedDataPasted,
  setEncryptedDataPasted,
  fileFormat,
  setFileFormat,
  onDecrypt,
  isDecrypting
}) => {
  const [error, setError] = useState("");
  const [fileLoaded, setFileLoaded] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleLoadEncryptedData = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const content = await readFileAsText(file);
        setEncryptedDataPasted(content);
        setFileLoaded(true);
        setError("");
      } catch (error) {
        console.error("Failed to read file:", error);
        setError("Could not load the file. Please try again.");
      }
    }
  };

  const handleEncryptKeyChange = (e) => {
    const value = e.target.value;
    setEncryptKey(value);

    if (value.length < 4) {
      setError("The key must be at least 4 characters long");
    } else {
      setError("");
    }
  };

  const handleDecrypt = () => {
    if (!encryptedDataPasted) {
      setError("Please provide encrypted data");
      return;
    }
    
    if (!encryptKey || encryptKey.length < 4) {
      setError("Please provide a valid decryption key");
      return;
    }
    
    setError("");
    onDecrypt();
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h5" gutterBottom>
        Decrypt Data
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Decryption Key"
            variant="outlined"
            fullWidth
            value={encryptKey}
            onChange={handleEncryptKeyChange}
            error={!!error && error.includes("key")}
            helperText={error && error.includes("key") ? error : "Enter the key used to encrypt the file"}
            type={showPassword ? "text" : "password"}
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                    aria-label="toggle password visibility"
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="file-format-label">File Format</InputLabel>
            <Select
              labelId="file-format-label"
              id="file-format"
              value={fileFormat}
              label="File Format"
              onChange={(e) => setFileFormat(e.target.value)}
            >
              <MenuItem value="PNG">PNG</MenuItem>
              <MenuItem value="JPG">JPG</MenuItem>
              <MenuItem value="JPEG">JPEG</MenuItem>
              <MenuItem value="GIF">GIF</MenuItem>
              <MenuItem value="MP4">MP4</MenuItem>
              <MenuItem value="PDF">PDF</MenuItem>
              <MenuItem value="TXT">TXT</MenuItem>
              <MenuItem value="MD">MD</MenuItem>
              <MenuItem value="DOC">DOC</MenuItem>
              <MenuItem value="DOCX">DOCX</MenuItem>
              <MenuItem value="HTML">HTML</MenuItem>
              <MenuItem value="JSON">JSON</MenuItem>
            </Select>
            <FormHelperText>Select the original file format</FormHelperText>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<FileUploadIcon />}
            fullWidth
            sx={{ mb: 2 }}
          >
            Load Encrypted Data File
            <input
              type="file"
              hidden
              accept=".txt"
              onChange={handleLoadEncryptedData}
            />
          </Button>
          
          {fileLoaded && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Encrypted data file loaded successfully!
            </Alert>
          )}
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Encrypted Data"
            multiline
            rows={6}
            fullWidth
            placeholder="Paste encrypted data here..."
            value={encryptedDataPasted}
            onChange={(e) => setEncryptedDataPasted(e.target.value)}
            error={!!error && error.includes("data")}
            helperText={error && error.includes("data") ? error : "Paste the encrypted data here or load from a file"}
            sx={{ mb: 2 }}
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            onClick={handleDecrypt}
            disabled={!encryptedDataPasted || !encryptKey || isDecrypting}
            startIcon={isDecrypting ? <CircularProgress size={20} color="inherit" /> : <LockOpenIcon />}
          >
            {isDecrypting ? "Decrypting..." : "Decrypt Data"}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default DecryptionForm;